import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { $createTextNode, $insertNodes } from "lexical";

import { useDebounce } from "hooks/useDebounce";
import UserService from "services/User";

import { $createMentionNode } from "../nodes/MentionNode";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * The typeahead menu, positioned against the caret in viewport coordinates.
 *
 * Lexical resolves its own anchor in document coordinates against
 * ``document.body``, which places the menu correctly only while nothing
 * between the body and the caret establishes a containing block. The note form
 * opens inside a dialog that does, so the menu landed far from the caret.
 * Fixed positioning read from the caret rect, portaled to the body, is what
 * the link hover card already does and does not depend on the ancestor chain.
 */
function MentionMenu({
  options,
  selectedIndex,
  selectOptionAndCleanUp,
  setHighlightedIndex,
}) {
  const listRef = useRef(null);

  // Measured after each query, because the caret has moved by a character every
  // time the list is rebuilt. Written straight to the element rather than held
  // in state, so the menu is placed before it is painted rather than after.
  useLayoutEffect(() => {
    const list = listRef.current;
    const selection = window.getSelection();
    if (!list || !selection || selection.rangeCount === 0) return;
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    // A collapsed range reports a zero-width rect, which is still positioned;
    // only a wholly empty one means there was nothing to measure.
    if (rect.top === 0 && rect.left === 0) return;
    list.style.left = `${rect.left}px`;
    list.style.top = `${rect.bottom + 4}px`;
    list.style.visibility = "visible";
  }, [options]);

  return createPortal(
    <ul
      ref={listRef}
      role="listbox"
      aria-label="Mention a user"
      style={{ position: "fixed", visibility: "hidden" }}
      className="tw:z-[1100] tw:max-h-60 tw:min-w-56 tw:overflow-y-auto tw:rounded-md tw:border tw:border-[var(--color-border)] tw:bg-[var(--color-surface-overlay)] tw:py-1 tw:shadow-lg"
    >
      {options.map((option, index) => (
        <li
          key={option.key}
          role="option"
          tabIndex={-1}
          aria-selected={selectedIndex === index}
          ref={option.setRefElement}
          className="tw:cursor-pointer tw:px-3 tw:py-1.5 tw:text-sm tw:text-[var(--color-text-primary)] tw:aria-selected:bg-[var(--color-warm-gray-light)]"
          onMouseEnter={() => setHighlightedIndex(index)}
          onMouseDown={event => event.preventDefault()}
          onClick={() => {
            setHighlightedIndex(index);
            selectOptionAndCleanUp(option);
          }}
        >
          {option.name}
        </li>
      ))}
    </ul>,
    document.body
  );
}

MentionMenu.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedIndex: PropTypes.number,
  selectOptionAndCleanUp: PropTypes.func.isRequired,
  setHighlightedIndex: PropTypes.func.isRequired,
};

export class MentionMenuOption extends MenuOption {
  constructor(name, userId) {
    super(String(userId));
    this.name = name;
    this.userId = userId;
  }
}

/**
 * Resolves the user list for the current typeahead query.
 *
 * A null query means the menu is closed. An empty string means the user
 * typed a bare `@`, which lists everyone; anything else searches.
 */
export function useMentionOptions(queryString) {
  const debouncedQuery = useDebounce(queryString, SEARCH_DEBOUNCE_MS);
  const { data: users } = useQuery({
    queryKey: ["mention-users", debouncedQuery],
    queryFn: () =>
      debouncedQuery
        ? UserService.search(debouncedQuery)
        : UserService.getUsers(),
    enabled: queryString !== null && debouncedQuery !== null,
  });

  return useMemo(
    () =>
      (users ?? []).map(user => new MentionMenuOption(user.full_name, user.id)),
    [users]
  );
}

export function $replaceWithMention(option, nodeToReplace) {
  const mention = $createMentionNode(option.name, option.userId);
  // The typeahead passes null when the trigger spans no single text node.
  if (nodeToReplace) {
    nodeToReplace.replace(mention);
  } else {
    $insertNodes([mention]);
  }
  const space = $createTextNode(" ");
  mention.insertAfter(space);
  space.select();
}

function MentionsPlugin() {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState(null);
  // minLength 0 so a bare "@" opens the menu with the full user list.
  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch("@", {
    minLength: 0,
  });
  const options = useMentionOptions(queryString);

  const onSelectOption = useCallback(
    (option, nodeToReplace, closeMenu) => {
      editor.update(() => {
        $replaceWithMention(option, nodeToReplace);
        closeMenu();
      });
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin
      options={options}
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForTriggerMatch}
      menuRenderFn={(anchorElementRef, menuProps) =>
        options.length > 0 ? (
          <MentionMenu options={options} {...menuProps} />
        ) : null
      }
    />
  );
}

export default MentionsPlugin;
